import { inject, Injectable } from '@angular/core';
import { GrowlerService } from '@app/components/core/growler/service/growler.service';
import { GrowlerPendingService } from '@app/service/base-growler/growler-pending.service';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';
import type { IContentStore } from '@app/interface/content/content-store.interface';
import { GROWLER_KEYS } from '@app/service/add-to-cart-growler/growler-content-keys';

export interface SubjectName {
  subjectFirstName: string;
}

interface CopySuccessPending {
  subjects: SubjectName[];
  items: string[];
}

@Injectable({ providedIn: 'root' })
export class CopyOrderGrowlerService extends GrowlerPendingService<CopySuccessPending> {
  private readonly growlerService = inject(GrowlerService);
  private readonly contentStore = inject<IContentStore>(angularjs.ContentStore);

  showSuccess(subjects: SubjectName[], items: string[]): void {
    if (subjects.length === 0 || items.length === 0) return;
    const title = this.formatSuccessHeadline(subjects);
    const formattedItems = items.map((name) =>
      this.contentStore.translate(GROWLER_KEYS.ITEM_LINE, { quantity: '1', itemName: name })
    );
    this.growlerService.success({ title, items: formattedItems, closeable: true, growlerStyle: 'floating' });
  }

  /**
   * Defer success growler for display after a route transition.
   * Call showPendingIfAny() once the cart page has initialized.
   */
  setPendingSuccess(subjects: SubjectName[], items: string[]): void {
    if (subjects.length === 0 || items.length === 0) return;
    this.setPendingData({ subjects, items });
  }

  protected showPending({ subjects, items }: CopySuccessPending): void {
    this.showSuccess(subjects, items);
  }

  showFailures(failedSubjects: SubjectName[]): void {
    if (failedSubjects.length === 0) return;

    const title =
      failedSubjects.length === 1
        ? this.contentStore.translate(GROWLER_KEYS.COPY_FAILED_SINGLE_TITLE, {
            studentName: failedSubjects[0].subjectFirstName
          })
        : this.contentStore.translate(GROWLER_KEYS.COPY_FAILED_MULTIPLE_TITLE);

    const message = this.contentStore.translate(GROWLER_KEYS.COPY_FAILED_MESSAGE);

    this.growlerService.warning({
      title,
      message,
      closeable: true,
      growlerStyle: 'floating'
    });
  }

  private formatSuccessHeadline(subjects: SubjectName[]): string {
    if (subjects.length >= 3) {
      return this.contentStore.translate(GROWLER_KEYS.COPY_SUCCESS_ALL_STUDENTS_HEADLINE);
    }
    const names = subjects.map((s) => s.subjectFirstName);
    const nameStr = names.length === 1 ? names[0] : `${names[0]} & ${names[1]}`;
    return this.contentStore.translate(GROWLER_KEYS.ADDED_TO_CART_HEADLINE, { names: nameStr });
  }
}
